import { z } from "zod";
import { SshKeyNameSchema, SshPrivateKeySchema } from "./provision.ts";

/**
 * Add a key to the library.
 *
 * Validated with Zod rather than an Elysia `t` body schema for the same reason
 * the remote-provision routes are: Elysia's validation error carries the
 * offending value, and here the offending value is a private key.
 */
export const CreateSshKeySchema = z.object({
	name: SshKeyNameSchema,
	/**
	 * Default login for hosts this key opens. Per-key because an operator with a
	 * root key and a `deploy` key should not have to remember which is which.
	 */
	sshUser: z.string().trim().min(1).max(64).default("root"),
	privateKey: SshPrivateKeySchema,
	/**
	 * Only for an encrypted key. An empty string is "no passphrase" rather than
	 * a passphrase of zero length — a browser form sends the former for a field
	 * the operator never touched.
	 */
	passphrase: z
		.string()
		.nullish()
		.transform((v) => (v && v.length > 0 ? v : null)),
});
export type CreateSshKeyInput = z.infer<typeof CreateSshKeySchema>;

/**
 * Rename only.
 *
 * Rotating a key means adding a new one: a key's identity is its key material,
 * and letting an edit swap that underneath the workers already linked to it
 * would silently change what a redeploy authenticates with.
 */
export const RenameSshKeySchema = z.object({ name: SshKeyNameSchema });
export type RenameSshKeyInput = z.infer<typeof RenameSshKeySchema>;

/**
 * What the API returns for a saved key — every field except the two that
 * matter. Declared as an interface, not derived from the Prisma row, so no
 * future column can widen the response by accident: `keyEnc` and
 * `passphraseEnc` must never appear in a response, and a shape that is written
 * out by hand cannot come to include them.
 */
export interface SshKeyMetadata {
	id: string;
	name: string;
	sshUser: string;
	/** `SHA256:…` for the key itself. Null when ssh-keygen could not read it. */
	fingerprint: string | null;
	createdAt: string;
	lastUsedAt: string | null;
	/** Hostnames of the workers holding this key. Empty means safe to delete. */
	usedBy: string[];
}
